import { LOGIN_LABELS } from "../constants";
import { loginRequest } from "../auth/msalConfig";
import msalInstance from "../auth/msalInstance";
import isInternalUser from "../utils/isInternalUser";
import { USBIconProfile } from "@usb-shield/react-icons";

/**
 * Context-aware login entry point. Renders a SINGLE login button whose label
 * and behavior are determined by the host the app was launched from:
 *  - Internal host (hostname contains "us.bank-dns.com") -> "Login with SSO",
 *    triggers the MSAL loginRedirect flow.
 *  - Any other host -> "Log In", triggers the existing CIAM flow via
 *    onExternalLogin.
 *
 * sessionStorage.loginType is set eagerly (on render), not just on click, so
 * that the very first page load already carries the correct flag before
 * App.js's ProtectedLayout decides which auth provider to mount. This avoids
 * an internal user ever falling through to the CIAM provider on first load.
 */
const LoginButton = ({ onExternalLogin }) => {
  const internal = isInternalUser();
  const loginType = internal ? "sso" : "external";

  // Eagerly keep sessionStorage.loginType in sync with the detected context,
  // as long as no in-progress flow has already claimed a different value.
  if (sessionStorage.getItem("loginType") !== loginType) {
    sessionStorage.setItem("loginType", loginType);
  }

  const handleSSOLogin = () => {
    sessionStorage.setItem("loginType", "sso");
    // MSAL is already initialized in index.js, just call loginRedirect
    msalInstance.loginRedirect(loginRequest).catch((err) => {
      console.error("[LoginButton] SSO login failed:", err);
    });
  };

  const handleExternalLogin = () => {
    // Explicitly mark the flow so a previously abandoned SSO attempt can't
    // leave loginType === "sso" and make ProtectedLayout mount the wrong provider.
    sessionStorage.setItem("loginType", "external");
    onExternalLogin?.();
  };

  const handleClick = internal ? handleSSOLogin : handleExternalLogin;
  const label = internal ? LOGIN_LABELS.sso : LOGIN_LABELS.external;

  return (
    <div
      data-testid="login"
      className="d-flex align-center me-4 cursor-pointer"
      onClick={handleClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          handleClick();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <span className="mt-5 ps-2">
        <USBIconProfile size={20} />
      </span>

      <span className="ps-2">{label}</span>
    </div>
  );
};

export default LoginButton;
